/**
 * KinsCircle Phase 6B — AI Usage Quotas
 *
 * STRICTLY ADDITIVE. ADVISORY-ONLY (client-side soft caps).
 * Honors STABILITY.md §12.2: hard server caps belong in the edge function;
 * this layer is a soft pre-flight check + UX surface so users see the limit
 * coming before they fire a wasted call.
 *
 * Stored in localStorage only. NEVER writes to Supabase. NEVER blocks normal
 * memory uploads — only AI generation is rate-limited.
 *
 * Daily / weekly / monthly windows are calendar-based (UTC midnight).
 *
 * If the usageLimits feature flag is OFF, all checks return `{ allowed: true }`
 * and the layer is a no-op.
 */

import { isAIUsageLimitsEnabled } from './featureFlag';
import type { AIAction } from './promptTemplates';

// ── Plan-based quotas ───────────────────────────────────────────────────────

export type AIQuotaPlan =
  | 'free'
  | 'monthly_premium'
  | 'annual_founding'
  | 'legacy_plus'
  | 'ai_plan'
  | 'staff';

export type AIQuotaWindow = 'day' | 'week' | 'month';

export type AIQuotaSpec = {
  day: number;
  week: number;
  month: number;
};

/**
 * Per-plan AI generation budgets. Story generations are the most expensive
 * action, so they have their own dedicated counter; lighter actions (title,
 * tags, tone) share a separate "lite" pool.
 *
 * Owners and admins (`staff`) effectively have no cap — but we still record
 * usage so it shows up on the diagnostics card.
 */
const STORY_QUOTAS: Record<AIQuotaPlan, AIQuotaSpec> = {
  free: { day: 0, week: 0, month: 0 },
  monthly_premium: { day: 8, week: 30, month: 80 },
  annual_founding: { day: 5, week: 20, month: 50 },
  legacy_plus: { day: 25, week: 100, month: 300 },
  ai_plan: { day: 25, week: 100, month: 300 },
  staff: { day: 1000, week: 5000, month: 20000 },
};

const LITE_QUOTAS: Record<AIQuotaPlan, AIQuotaSpec> = {
  free: { day: 0, week: 0, month: 0 },
  monthly_premium: { day: 40, week: 150, month: 400 },
  annual_founding: { day: 25, week: 100, month: 250 },
  legacy_plus: { day: 100, week: 400, month: 1200 },
  ai_plan: { day: 100, week: 400, month: 1200 },
  staff: { day: 1000, week: 5000, month: 20000 },
};

const isStoryAction = (a: AIAction): boolean =>
  a === 'story' || a === 'timeline' || a === 'on_this_day';

const isLiteAction = (a: AIAction): boolean =>
  a === 'title' || a === 'tags' || a === 'tone';

// ── Counter storage ─────────────────────────────────────────────────────────

const QUOTA_LS_KEY = 'kinscircle.ai.quota.counters.v1';

type QuotaEntry = {
  ts: number;
  bucket: 'story' | 'lite';
};

type QuotaState = {
  // Sliding window of recent successful AI starts. Capped at 500.
  entries: QuotaEntry[];
};

const readState = (): QuotaState => {
  if (typeof window === 'undefined') return { entries: [] };
  try {
    const raw = window.localStorage.getItem(QUOTA_LS_KEY);
    if (!raw) return { entries: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.entries)) return { entries: [] };
    return { entries: parsed.entries.slice(-500) };
  } catch {
    return { entries: [] };
  }
};

const writeState = (s: QuotaState): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const trimmed: QuotaState = { entries: s.entries.slice(-500) };
    window.localStorage.setItem(QUOTA_LS_KEY, JSON.stringify(trimmed));
  } catch {
    /* non-fatal */
  }
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;

const countInWindow = (
  entries: QuotaEntry[],
  bucket: 'story' | 'lite',
  windowMs: number,
): number => {
  const now = Date.now();
  return entries.filter(
    (e) => e.bucket === bucket && now - e.ts <= windowMs,
  ).length;
};

// ── Plan resolution ─────────────────────────────────────────────────────────

export const resolveQuotaPlan = (
  role: string | null | undefined,
  plan: string | null | undefined,
): AIQuotaPlan => {
  if (role === 'owner' || role === 'admin') return 'staff';
  if (plan === 'monthly_premium') return 'monthly_premium';
  if ((plan === 'annual_founding' || plan === 'annual_standard')) return 'annual_founding';
  if (plan === 'legacy_plus') return 'legacy_plus';
  if (plan === 'ai_plan') return 'ai_plan';
  return 'free';
};

// ── Public API ──────────────────────────────────────────────────────────────

export type AIQuotaCheck =
  | { allowed: true }
  | {
      allowed: false;
      window: AIQuotaWindow;
      used: number;
      limit: number;
      bucket: 'story' | 'lite';
      message: string;
    };

/**
 * Pre-flight quota check. Returns `{ allowed: true }` when:
 *   - the usageLimits flag is OFF, OR
 *   - the action's bucket is under all three (day/week/month) caps for
 *     the resolved plan.
 * Otherwise returns the specific window that ran out, with a user-safe message.
 */
export const checkAIQuota = (
  action: AIAction,
  role: string | null | undefined,
  plan: string | null | undefined,
): AIQuotaCheck => {
  if (!isAIUsageLimitsEnabled()) return { allowed: true };

  const quotaPlan = resolveQuotaPlan(role, plan);
  const bucket: 'story' | 'lite' = isStoryAction(action)
    ? 'story'
    : isLiteAction(action)
    ? 'lite'
    : 'lite';

  const spec = bucket === 'story' ? STORY_QUOTAS[quotaPlan] : LITE_QUOTAS[quotaPlan];
  const { entries } = readState();

  const dayUsed = countInWindow(entries, bucket, DAY_MS);
  if (dayUsed >= spec.day) {
    return {
      allowed: false,
      window: 'day',
      used: dayUsed,
      limit: spec.day,
      bucket,
      message: 'You\'ve reached your AI generation limit for now. Try again tomorrow.',
    };
  }
  const weekUsed = countInWindow(entries, bucket, WEEK_MS);
  if (weekUsed >= spec.week) {
    return {
      allowed: false,
      window: 'week',
      used: weekUsed,
      limit: spec.week,
      bucket,
      message: 'You\'ve reached your AI generation limit for now. Your weekly budget refreshes soon.',
    };
  }
  const monthUsed = countInWindow(entries, bucket, MONTH_MS);
  if (monthUsed >= spec.month) {
    return {
      allowed: false,
      window: 'month',
      used: monthUsed,
      limit: spec.month,
      bucket,
      message: 'You\'ve reached your AI generation limit for now. Your monthly budget refreshes next cycle.',
    };
  }

  return { allowed: true };
};

/** Record a successful (or attempted) AI call against the quota counters. */
export const recordAIQuotaUsage = (action: AIAction): void => {
  if (!isAIUsageLimitsEnabled()) return;
  const bucket: 'story' | 'lite' = isStoryAction(action) ? 'story' : 'lite';
  const s = readState();
  s.entries.push({ ts: Date.now(), bucket });
  writeState(s);
};

/** Read the current quota usage summary for diagnostics display. */
export type AIQuotaSummary = {
  plan: AIQuotaPlan;
  story: { day: number; week: number; month: number; cap: AIQuotaSpec };
  lite: { day: number; week: number; month: number; cap: AIQuotaSpec };
};

export const readAIQuotaSummary = (
  role: string | null | undefined,
  plan: string | null | undefined,
): AIQuotaSummary => {
  const quotaPlan = resolveQuotaPlan(role, plan);
  const { entries } = readState();
  return {
    plan: quotaPlan,
    story: {
      day: countInWindow(entries, 'story', DAY_MS),
      week: countInWindow(entries, 'story', WEEK_MS),
      month: countInWindow(entries, 'story', MONTH_MS),
      cap: STORY_QUOTAS[quotaPlan],
    },
    lite: {
      day: countInWindow(entries, 'lite', DAY_MS),
      week: countInWindow(entries, 'lite', WEEK_MS),
      month: countInWindow(entries, 'lite', MONTH_MS),
      cap: LITE_QUOTAS[quotaPlan],
    },
  };
};

/** Owner/admin diagnostics-only utility. Never affects DB rows. */
export const resetAIQuotaCounters = (): void => {
  writeState({ entries: [] });
};
